import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Building, BarChart3, Settings, Shield, Plus, 
  TrendingUp, Activity, Globe, RefreshCw, AlertCircle, 
  ArrowRight, ExternalLink, HelpCircle
} from 'lucide-react';

const ClientFacingTools = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    totalClientUsers: 0,
    activeUsers: 0,
    totalBusinessUnits: 0,
    activeBusinessUnits: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const [clientUsersResponse, businessUnitsResponse] = await Promise.all([
        fetch('/api/client-users', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('/api/business-units', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);
      
      if (!clientUsersResponse.ok || !businessUnitsResponse.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const clientUsersData = await clientUsersResponse.json();
      const businessUnitsData = await businessUnitsResponse.json();
      
      const clientUsers = clientUsersData.clientUsers || [];
      const businessUnits = businessUnitsData || [];
      
      // Generate recent activity based on real data
      const recentActivity = [
        {
          id: 1,
          type: 'info',
          message: `${clientUsers.length} client users configured across ${businessUnits.length} business units`,
          time: 'System Status',
          icon: <Users className="w-4 h-4 text-blue-400" />
        },
        {
          id: 2,
          type: 'success',
          message: `${clientUsers.filter(u => u.is_active).length} active client users ready for portal access`,
          time: 'Active Users',
          icon: <Activity className="w-4 h-4 text-green-400" />
        },
        {
          id: 3,
          type: 'info',
          message: `${businessUnits.filter(bu => bu.is_active).length} business units with active portals`,
          time: 'Portal Status',
          icon: <Globe className="w-4 h-4 text-purple-400" />
        }
      ];
      
      setDashboardData({
        totalClientUsers: clientUsers.length,
        activeUsers: clientUsers.filter(u => u.is_active).length,
        totalBusinessUnits: businessUnits.length,
        activeBusinessUnits: businessUnits.filter(bu => bu.is_active).length,
        recentActivity
      });
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Some features may not be available.');
    } finally {
      setLoading(false);
    }
  };

  const toolCategories = [
    {
      title: 'Client Portal Management',
      description: 'Core client portal administration and management tools',
      tools: [
        {
          title: 'Client Users',
          icon: <Users className="w-6 h-6" />,
          description: 'Create, manage, and configure client user accounts',
          path: '/tiles/client_facing_tools/client-users',
          status: 'Active',
          color: 'bg-green-600 hover:bg-green-700',
          primary: true
        },
        {
          title: 'Business Units',
          icon: <Building className="w-6 h-6" />,
          description: 'Manage business units and their portal configurations',
          path: '/tiles/client_facing_tools/business-units',
          status: 'Active',
          color: 'bg-orange-600 hover:bg-orange-700',
          primary: true
        },
        {
          title: 'Portal Analytics',
          icon: <BarChart3 className="w-6 h-6" />,
          description: 'View client portal usage, login statistics, and activity analytics',
          path: '/tiles/client_facing_tools/analytics',
          status: 'Active',
          color: 'bg-blue-600 hover:bg-blue-700'
        },
        {
          title: 'Access Control',
          icon: <Shield className="w-6 h-6" />,
          description: 'Manage client portal access rights and permissions',
          path: '/tiles/client_facing_tools/access',
          status: 'Available',
          color: 'bg-indigo-600 hover:bg-indigo-700'
        },
        {
          title: 'Portal Settings',
          icon: <Settings className="w-6 h-6" />,
          description: 'Configure portal appearance, features, and branding',
          path: '/tiles/client_facing_tools/settings',
          status: 'Available',
          color: 'bg-purple-600 hover:bg-purple-700'
        }
      ]
    }
  ];

  const quickActions = [
    {
      title: 'Create Client User',
      description: 'Add a new client user account',
      action: () => navigate('/tiles/client_facing_tools/client-users'),
      icon: <Plus className="w-5 h-5" />,
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: 'View Analytics',
      description: 'Check portal usage statistics',
      action: () => navigate('/tiles/client_facing_tools/analytics'),
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      title: 'Manage Access',
      description: 'Configure portal access permissions',
      action: () => navigate('/tiles/client_facing_tools/access'),
      icon: <Shield className="w-5 h-5" />,
      color: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-900/30 text-green-400 border border-green-800">Active</span>;
      case 'Available':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-900/30 text-blue-400 border border-blue-800">Available</span>;
      case 'Beta':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-900/30 text-yellow-400 border border-yellow-800">Beta</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-700 text-slate-400 border border-slate-600">{status}</span>;
    }
  };

  const handleToolClick = (tool) => {
    navigate(tool.path);
  };

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 h-[calc(100vh-120px)] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-300">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading Client Facing Tools...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 h-[calc(100vh-120px)] overflow-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-200 mb-2">Client Portal Management</h2>
          <p className="text-slate-400">Manage client portals, user accounts, and customer-facing services</p>
        </div>
        <button 
          onClick={fetchDashboardData}
          className="text-slate-400 hover:text-slate-300 p-2 rounded-lg hover:bg-slate-700"
          title="Refresh Data"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-800 rounded-lg flex items-center gap-3 text-yellow-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-400">{dashboardData.totalClientUsers}</div>
              <div className="text-sm text-slate-400">Total Client Users</div>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-400">{dashboardData.activeUsers}</div>
              <div className="text-sm text-slate-400">Active Users</div>
            </div>
            <Activity className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-purple-400">{dashboardData.activeBusinessUnits}</div>
              <div className="text-sm text-slate-400">Active Portals</div>
            </div>
            <Globe className="w-8 h-8 text-purple-400" />
          </div>
        </div>
        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-orange-400">{dashboardData.totalBusinessUnits}</div>
              <div className="text-sm text-slate-400">Business Units</div>
            </div>
            <Building className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className={`${action.color} text-white p-4 rounded-lg hover:shadow-lg transition-all duration-200 flex items-center gap-3`}
            >
              <div className="p-2 bg-white/20 rounded">
                {action.icon}
              </div>
              <div className="text-left">
                <div className="font-medium">{action.title}</div>
                <div className="text-sm opacity-90">{action.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tool Categories */}
      {toolCategories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-8">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-200 mb-1">{category.title}</h3>
            <p className="text-slate-400 text-sm">{category.description}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {category.tools.map((tool, index) => (
              <div
                key={index}
                onClick={() => handleToolClick(tool)}
                className={`bg-slate-700/30 border border-slate-700 rounded-lg p-4 hover:bg-slate-700/50 hover:border-slate-600 transition-all duration-200 cursor-pointer group ${tool.primary ? 'ring-1 ring-blue-500/20' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`${tool.color} p-2 rounded-lg text-white group-hover:shadow-lg transition-shadow`}>
                    {tool.icon}
                  </div>
                  {getStatusBadge(tool.status)}
                </div>
                
                <h4 className="text-lg font-medium text-slate-200 mb-2 group-hover:text-white transition-colors">
                  {tool.title}
                </h4>
                
                <p className="text-slate-400 text-sm mb-3 group-hover:text-slate-300 transition-colors">
                  {tool.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-blue-400 group-hover:text-blue-300 text-sm font-medium transition-colors">
                    Open Tool
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* System Status */}
      <div className="bg-slate-700/30 border border-slate-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          System Status
        </h3>
        <div className="space-y-3">
          {dashboardData.recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-700/50">
              <div>{activity.icon}</div>
              <div className="flex-1">
                <div className="text-slate-300 text-sm">{activity.message}</div>
                <div className="text-slate-500 text-xs">{activity.time}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientFacingTools;